import { datadogLogs } from '@datadog/browser-logs';

import { IFrontEndConfiguration } from 'generated/sanity-graphql';

import { appVersionCode, brand, env, isLocalDev, isStoryBook, platform } from './environment';

declare global {
  interface Window {
    frontEndConfiguration?: IFrontEndConfiguration;
    frontEndConfigurationError?: string;
  }
}

const getError = () => {
  if (window.frontEndConfigurationError) {
    return window.frontEndConfigurationError;
  }

  if (!window.frontEndConfiguration) {
    return (
      'Something has gone wrong while instantiating front end configuration. ' +
      'Check the network tab to see if a script called `front-end-configuration was fetched'
    );
  }

  return null;
};

/**
 * We are suffering from a bit of chicken vs egg problem...
 * When we detect an error with the dynamic FE config, we want to log an error to DD.
 * However, to log to DD we need it to be initialized. Eventually this means that there
 * will be a hard cyclic dependency between a valid FE config and DD.
 *
 * To avoid the cycle for now, this code waits for DD to be ready before logging any such
 * error to the service. We will need to rethink this approach once all configuration
 * values are sourced from the dynamic FE configuration.
 */
export const validateFrontEndConfiguration = () => {
  const error = getError();

  datadogLogs.onReady(() => {
    if (error && !isLocalDev) {
      const ctx = {
        appVersionCode: appVersionCode(),
        brand: brand(),
        platform: platform(),
        stage: env(),
        reason: 'front end configuration failure',
      };

      datadogLogs.logger.error(error, ctx);
    }
  });
};

export const getFrontEndConfiguration = () => {
  const error = getError();
  if (error) {
    if (!isStoryBook && isLocalDev) {
      throw Error(error);
    } else {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  return window.frontEndConfiguration;
};

type ConfigKeys = NonNullable<IFrontEndConfiguration>;

export const getConfigValue = <K extends keyof ConfigKeys>(key: K) => {
  const feConfig = getFrontEndConfiguration();
  return feConfig?.[key] || null;
};

type ApiKeys = NonNullable<IFrontEndConfiguration['apiKeys']>;

export const getApiKey = <K extends keyof ApiKeys>(key: K, fallback = '') => {
  const keys = getConfigValue('apiKeys');
  if (!keys) {
    return fallback;
  }
  return keys[key] ?? fallback;
};
